<template>
  <ThePage>
    <template #pageTitle>
      {{ i18n(translations.pageTitle) }}
    </template>
    <template #pageContent>
      <div class="mx-auto w-4/5 px-56">
        <RevCard class="p-32">
          <RevStepper
            :active-step
            alternative-text-back="back"
            alternative-text-close="close"
            alternative-text-completed="completed"
            :alternative-text-current="activeStep"
            class="mt-3"
            :has-back="activeStepIndex > 0"
            :steps="STEPS"
            @back="goToPreviousStep"
          />
          <CategoryChoiceStep
            v-if="isActiveStep(STEPS[0].name)"
            :active-category="selectedCategory"
            :is-loading-categories="productCategoriesStatus === 'pending'"
            :product-categories="productCategories || []"
            @submit="handleSubmitCategory"
          />
          <ProductDetailsStep
            v-if="isActiveStep(STEPS[1].name)"
            :current-product-details="createdProductDetails"
            :last-step-errors
            :product
            :selected-category
            @submit="handleSubmitProductDetails"
          />
          <ListingDetailsStep
            v-if="isActiveStep(STEPS[2].name)"
            :last-step-current-values
            :product-details="createdProductDetails"
            @on-error="handleSubmitListingDetailsError"
          />
        </RevCard>
      </div>
    </template>
  </ThePage>
</template>

<script setup lang="ts">
import { useAsyncData, useRoute } from '#imports'
import { computed, ref } from 'vue'

import {
  HttpApiError,
  type ProblemValidationErrorMember,
} from '@backmarket/http-api'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevCard } from '@ds/components/Card'
import { RevStepper } from '@ds/components/Stepper'

import type { GetProductResponse, ProductCategory } from '~/api/products/types'

import translations from './ProductCreate.translations'
import CategoryChoiceStep from './components/steps/CategoryChoiceStep.vue'
import ListingDetailsStep from './components/steps/ListingDetailsStep.vue'
import ProductDetailsStep from './components/steps/ProductDetailsStep.vue'

const $httpFetch = useHttpFetch()
const route = useRoute()
const i18n = useI18n()
const { openErrorToast } = useTheToast()
const selectedCategory = ref<ProductCategory | null>(null)
const createdProductDetails = ref<Record<string, unknown> | null>(null)
const product = ref<GetProductResponse | null>(null)
const lastStepErrors = ref<ProblemValidationErrorMember[] | null>(null)
const lastStepCurrentValues = ref<Record<string, unknown> | null>(null)

const STEPS = [
  { name: 'categoryChoice', label: i18n(translations.categoryChoiceStep) },
  { name: 'productDetails', label: i18n(translations.productDetailsStep) },
  { name: 'listingDetails', label: i18n(translations.listingDetailsStep) },
]
const activeStepIndex = ref(0)

const activeStep = computed(() => {
  return STEPS[activeStepIndex.value].name
})

function isActiveStep(stepName: string) {
  return activeStep.value === stepName
}

function goToPreviousStep() {
  if (activeStepIndex.value === 0) {
    return
  }

  activeStepIndex.value -= 1

  window.scrollTo({ top: 0, behavior: 'smooth' })
}

function goToNextStep() {
  if (activeStepIndex.value === STEPS.length - 1) {
    return
  }

  activeStepIndex.value += 1

  window.scrollTo({ top: 0, behavior: 'smooth' })
}
function handleSubmitCategory(category: ProductCategory) {
  selectedCategory.value = category
  goToNextStep()
}
function handleSubmitProductDetails(productDetails: Record<string, unknown>) {
  createdProductDetails.value = productDetails
  goToNextStep()
}

function handleSubmitListingDetailsError({
  errors,
  currentValues,
}: {
  errors: ProblemValidationErrorMember[]
  currentValues: Record<string, unknown>
}) {
  lastStepErrors.value = errors
  lastStepCurrentValues.value = currentValues
  goToPreviousStep()
}

const {
  data: productCategories,
  status: productCategoriesStatus,
  error: productCategoriesError,
} = await useAsyncData(() => $httpFetch('/api/seller-experience/categories'))

if (productCategoriesError.value) {
  openErrorToast({
    content:
      HttpApiError.fromAnyError(productCategoriesError.value).title || '',
  })
}

if (route.query.productId) {
  const { data: productData, error: productError } = await useAsyncData(() =>
    $httpFetch('/api/seller-experience/products/:productId', {
      pathParams: {
        productId: route.query.productId as string,
      },
    }),
  )

  product.value = productData.value || null

  if (productError.value) {
    openErrorToast({
      content: HttpApiError.fromAnyError(productError.value).title || '',
    })
  }

  const productCategory = productCategories.value?.find(
    (category) => category.value === productData.value?.categoryId,
  )

  if (productCategory) {
    handleSubmitCategory(productCategory)
    // Transform product data to match ProductDetailsStep form values
    const transformedProduct = {
      ...productData.value?.fields,
      mainImageUrl: productData.value?.thumbnails?.[0] || '',
      imageUrl2: productData.value?.thumbnails?.[1] || '',
      imageUrl3: productData.value?.thumbnails?.[2] || '',
      imageUrl4: productData.value?.thumbnails?.[3] || '',
      imageUrl5: productData.value?.thumbnails?.[4] || '',
      imageUrl6: productData.value?.thumbnails?.[5] || '',
    }
    createdProductDetails.value = transformedProduct
  } else {
    openErrorToast({
      content: i18n(translations.productCategoryNotFound),
    })
  }
}
</script>
